import { GLOBAL, ReferenceByMAEC } from '@resistapp/common/api-types';
import { L2Targets } from '@resistapp/common/assays';
import { AllProjectEnvironmentTypesGroup } from '@resistapp/common/comparable-env-groups';
import { EnvironmentSubType, EnvironmentType } from '@resistapp/common/environment-types';
import { hasConsistentBioGroupNumbers, hasDuplicateBioNumbers } from '@resistapp/common/environments';
import {
  FullProject,
  FullSample,
  normalisationToAbundanceMode,
  XAxisMode,
  type Environment,
  type SampleStatus,
} from '@resistapp/common/types';
import { flattenSamplesByUID, getSampleChipIds, getSampleSortValue } from '@resistapp/common/utils';
import { chain, get, values } from 'lodash';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getEnvironmentsWithStatusForSelectedEnvTypeGroup } from '../components/plots/bar-box-map/utils';
import { buildResearchPlotData, ResearchPlotData } from '../data-utils/plot-data/research-plot-data';
import { QueryFilters } from '../hooks/use-query-filters/use-query-filters';
import { ResearchAdminFeatures, useResearchAdmin } from '../hooks/use-research-admin';
import { calculateWeightedReferenceMean } from '../utils/reference-line-utils';
import { useAssayContext } from './assay-context';
import { useSampleDataContext } from './sample-data-context';

export interface SampleSelectionState {
  ids: number[];
  removeOldSelections: boolean;
}

interface BaseResearch {
  error: Error | null;
  loading: boolean;
  queryFilters: QueryFilters;
  plotData: ResearchPlotData | null;
  project: FullProject | null;
  setGraphAndLegendContainerWidth: (width: number) => void;
  graphAndLegendContainerWidth: number;
  samplesBeingSelected: SampleSelectionState;
  setSamplesBeingSelected: (value: SampleSelectionState) => void;
  sampleStatusById: Record<string, SampleStatus>;
  samplesByEnvId: Record<number, Array<FullProject['samplesByUID'][string][number]>>;
  environmentsForSelectedEnvTypeGroup: Environment[];
  focusedSamplesByEnvId: Record<number, Array<FullProject['samplesByUID'][string][number]>>;
  adminFeatures: ResearchAdminFeatures;
  sequentialSampleIndexes: Record<number, number>;
  consistentBioGroupNumbering: boolean;
  consistentBioNumbering: boolean;
  referenceData: ReferenceByMAEC | undefined;
  getReferenceLevel: () => [number | null, EnvironmentType | EnvironmentSubType | null];
  tooManySamplesToShow: boolean;
}

interface ResearchLoading extends BaseResearch {
  loading: true;
  plotData: null;
  project: null;
  againstTime: false;
}

interface ResearchLoaded extends BaseResearch {
  loading: false;
  plotData: ResearchPlotData | null;
  project: FullProject;
  againstTime: boolean;
}

const ResearchContext = createContext<ResearchLoading | ResearchLoaded | undefined>(undefined);

export function ResearchProvider({ children }: { children: React.ReactNode }) {
  const { data: project, queryFilters, loading, error } = useSampleDataContext();
  const { allGeneGroups, getGroup, assaysLoaded } = useAssayContext();
  const adminFeatures = useResearchAdmin();

  const [graphAndLegendContainerWidth, setGraphAndLegendContainerWidth] = useState(0);
  const [samplesBeingSelected, setSamplesBeingSelectedLocal] = useState<SampleSelectionState>({
    ids: [],
    removeOldSelections: false,
  });
  const sampleRef = useRef<SampleSelectionState>({
    ids: [],
    removeOldSelections: false,
  });
  sampleRef.current = samplesBeingSelected;

  const projectIsValid = project && validateProject(project);

  // Build a sample dataset for legend purposes: apply chip filtering but ignore focused environment selections so that
  // the legend always lists all environments of the chosen environment type group irrespective of individual sample
  // selections/searches.
  const samplesByUIDForLegend = useMemo(() => {
    if (!project) return {};

    const { chips } = queryFilters.filters;
    // If all chips are selected (or chip filtering disabled), return the full dataset
    if (!chips.length || chips.length === project.chips.length) {
      return project.samplesByUID;
    }

    // Otherwise, build a filtered copy that only contains samples belonging to the selected chips.
    // Use getSampleChipIds so that a sample appears under every chip that has any CTS rows.
    return chain(project.samplesByUID)
      .mapValues(replicates =>
        replicates.filter(sample => {
          const sampleChipIds = getSampleChipIds([sample]);
          return sampleChipIds.some(id => chips.includes(id));
        }),
      )
      .pickBy(replicates => replicates.length > 0)
      .value();
  }, [project, queryFilters.filters]);

  const environmentsForSelectedEnvTypeGroup = useMemo(
    () =>
      getEnvironmentsWithStatusForSelectedEnvTypeGroup(
        samplesByUIDForLegend,
        queryFilters.filters.selectedEnvironmentTypeGroup,
        project?.id,
      ),
    [samplesByUIDForLegend, queryFilters.filters.selectedEnvironmentTypeGroup, project?.id],
  );
  const sampleStatusById = useMemo(
    () =>
      chain(environmentsForSelectedEnvTypeGroup)
        .keyBy(env => `${env.id}`)
        .mapValues(env => env.sampleStatus)
        .value(),
    [environmentsForSelectedEnvTypeGroup],
  );

  const selectedEnvironments = queryFilters.filters.selectedEnvironmentIdsOrdered;
  const toggleEnvironmentStable = queryFilters.toggleEnvironmentStable;
  // Clean up non-existent environments from URL
  useEffect(() => {
    if (!environmentsForSelectedEnvTypeGroup.length || !selectedEnvironments.length) {
      return;
    }

    // Clean up if there are any selected environments to ensure they exist abd are if expected type
    const envIds = environmentsForSelectedEnvTypeGroup.map(env => env.id);
    const validEnvironments = selectedEnvironments.filter(id => envIds.includes(id));
    // Only update if we found invalid environments
    if (validEnvironments.length !== selectedEnvironments.length) {
      toggleEnvironmentStable(undefined, true);
    }
  }, [selectedEnvironments, toggleEnvironmentStable, environmentsForSelectedEnvTypeGroup]);

  const consistentBioGroupNumbering = useMemo(() => {
    return project ? hasConsistentBioGroupNumbers(project.samplesByUID) : true;
  }, [project]);
  const consistentBioNumbering = useMemo(() => {
    return project ? !hasDuplicateBioNumbers(project.samplesByUID) : true;
  }, [project]);

  // PREPARE RESEARCH PLOT DATA
  const plotData = useMemo(() => {
    // TODO this is heavy and should be run only once on load: when filters change.
    // This would be to avoid double runing it first on project load, and then immediatelly again
    // because filters.selectedEnvironmentNamesOrdered depends on the project.
    return projectIsValid && assaysLoaded
      ? buildResearchPlotData(project, queryFilters.filters, allGeneGroups, getGroup)
      : null;
  }, [projectIsValid, assaysLoaded, project, queryFilters.filters, allGeneGroups, getGroup]);

  // ORDINAL DATA SORTING
  const sequentialSampleIndexes = useMemo(() => {
    if (!project?.samplesByUID) return {};

    const allSamples = flattenSamplesByUID(project.samplesByUID);
    const sortedSamples = [...allSamples].sort(
      (a, b) => getSampleSortValue(a, project.id) - getSampleSortValue(b, project.id),
    );

    const indexMap: Record<number, number> = {};
    sortedSamples.forEach((sample, index) => {
      indexMap[sample.id] = index + 1;
    });

    return indexMap;
  }, [project?.samplesByUID, project?.id]);

  const samplesByEnvId = useMemo(
    () =>
      chain(project?.samplesByUID || {})
        .values()
        .flatten()
        .groupBy(sample => sample.environment.id)
        .mapValues(samples => samples)
        .value(),
    [project?.samplesByUID],
  );

  const tooManySamplesToShow = useMemo(() => {
    if (!plotData || !graphAndLegendContainerWidth) return false;
    const count = plotData.boxData?.length || 0;
    if (!count) return false;
    const showBioRepIndicator = queryFilters.filters.xAxisMode === XAxisMode.ORDINAL_INDIVIDUAL;
    const thresholdPerSample = showBioRepIndicator ? 34 : 24;
    return graphAndLegendContainerWidth / count < thresholdPerSample;
  }, [plotData, graphAndLegendContainerWidth, queryFilters.filters.xAxisMode]);

  // Prevent repeatedly auto-selecting the first chip when the user explicitly cleared the selection.
  const hasAutoSelectedChipRef = useRef(false);

  const setSamplesBeingSelected = (newSelection: SampleSelectionState) => {
    if (newSelection.ids.length === 0) {
      setSamplesBeingSelectedLocal({ ids: [], removeOldSelections: newSelection.removeOldSelections });
    } else {
      setSamplesBeingSelectedLocal(newSelection);
    }
  };

  useEffect(() => {
    if (hasAutoSelectedChipRef.current) return;
    if (!project || project.chips.length <= 1 || !tooManySamplesToShow) return;

    const allEnvSelected =
      queryFilters.filters.selectedEnvironmentTypeGroup === AllProjectEnvironmentTypesGroup.ALL_PROJECT_ENVIRONMENTS &&
      queryFilters.filters.selectedEnvironmentIdsOrdered.length === Object.keys(samplesByEnvId).length;

    const allChipsSelected = queryFilters.filters.chips.length === project.chips.length;

    if (allEnvSelected && allChipsSelected) {
      queryFilters.setChipStable(project.chips[0].id);
      hasAutoSelectedChipRef.current = true;
    }
  }, [project, tooManySamplesToShow, samplesByEnvId, queryFilters]);

  const focusedSamplesByEnvId = useMemo(
    () =>
      chain(project?.focusedByUID || project?.samplesByUID || {})
        .values()
        .flatten()
        .groupBy(sample => sample.environment.id)
        .mapValues(samples => samples)
        .value(),
    [project?.focusedByUID, project?.samplesByUID],
  );

  // REFERENCE LEVEL GETTER
  const { selectedTargets, selectedEnvironmentIdsOrdered } = queryFilters.filters;
  const getReferenceLevel = useCallback((): [number | null, EnvironmentType | EnvironmentSubType | null] => {
    // Early exit if reference data or plot data isn't available
    if (!project?.researchReferencesByMAEC || !plotData) {
      return [null, null];
    }

    const { normalisationMode } = plotData;
    const referenceMetric = normalisationToAbundanceMode(normalisationMode);
    if (referenceMetric === null) {
      return [null, null];
    }

    // Determine environment type or subtype; return null if multiple
    const environmentTypes = chain(selectedEnvironmentIdsOrdered)
      .map(envId => {
        const samples = get(focusedSamplesByEnvId, envId, []);
        return samples.length > 0 ? samples[0].environment.type : null;
      })
      .filter(type => !!type)
      .uniq()
      .value();
    const environmentSubtypes = chain(selectedEnvironmentIdsOrdered)
      .map(envId => {
        const samples = get(focusedSamplesByEnvId, envId, []);
        return samples.length > 0 ? samples[0].environment.subtype : null;
      })
      .filter(type => !!type)
      .uniq()
      .compact()
      .value();
    if (environmentTypes.length !== 1) {
      return [null, null];
    }
    const environmentTypeOrSubtype = environmentSubtypes.length === 1 ? environmentSubtypes[0] : environmentTypes[0];

    // Determine which gene grouping to use
    const l2TargetSet = new Set(values(L2Targets));
    const selectedL2Targets = selectedTargets.filter(group => l2TargetSet.has(group as L2Targets)) as L2Targets[];

    // Single target - use direct lookup
    if (selectedL2Targets.length === 1) {
      const refLevel = get(
        project.researchReferencesByMAEC,
        [referenceMetric, selectedL2Targets[0], environmentTypeOrSubtype, GLOBAL, 'mean'],
        null,
      ) as number | null;
      return [refLevel, environmentTypeOrSubtype];
    }

    // Multiple targets - use weighted mean calculation
    if (selectedL2Targets.length > 1) {
      const firstFocusedSample: FullSample | undefined = values(project.focusedByUID || project.samplesByUID)[0]?.[0];

      // Use all analysed genes to weight the antibiotics, instead of only detected genes.
      // This makes the reference line deviate slightly from the mean of the a sample, even if the reference level is calculated from only that sample.
      // Using the detected genes in the focused samples would make the reference level flucturate when browsing between samples even within the same environment subtype.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      const selectedAssays: string[] = firstFocusedSample?.abundances.map(abundance => abundance.assay) || [];

      // You can uncomment this and limit reference calculation to 1 sample (per subtype), to verify that reference levels
      // align with box plot sample mean calculation.
      // const selectedAssays: string[] = firstFocusedSample
      //   ? firstFocusedSample.abundances
      //       .filter(abundance => getNormalisedValue(abundance, normalisationMode) != null)
      //       .map(abundance => abundance.assay)
      //   : [];

      const refLevel = calculateWeightedReferenceMean(
        project.researchReferencesByMAEC,
        referenceMetric,
        selectedL2Targets,
        environmentTypeOrSubtype,
        selectedAssays,
        getGroup,
      );
      return [refLevel, environmentTypeOrSubtype];
    }

    return [null, null];
  }, [project, plotData, selectedTargets, selectedEnvironmentIdsOrdered, focusedSamplesByEnvId, getGroup]);

  if (projectIsValid && !loading && assaysLoaded) {
    const contextData = {
      loading: false as const,
      plotData,
      project,
      queryFilters,
      error,
      setGraphAndLegendContainerWidth,
      graphAndLegendContainerWidth,
      samplesBeingSelected,
      setSamplesBeingSelected,
      againstTime: plotData?.againstTime || false,
      sampleStatusById,
      samplesByEnvId,
      focusedSamplesByEnvId,
      allEnvironments: environmentsForSelectedEnvTypeGroup,
      environmentsForSelectedEnvTypeGroup,
      adminFeatures,
      sequentialSampleIndexes,
      consistentBioGroupNumbering,
      consistentBioNumbering,
      referenceData: project.researchReferencesByMAEC,
      getReferenceLevel,
      tooManySamplesToShow,
    };
    return <ResearchContext.Provider value={contextData}>{children}</ResearchContext.Provider>;
  } else {
    const contextData = {
      loading: true as const,
      plotData: null,
      project: null,
      queryFilters,
      error,
      setGraphAndLegendContainerWidth,
      graphAndLegendContainerWidth,
      samplesBeingSelected,
      setSamplesBeingSelected,
      againstTime: false as const,
      sampleStatusById,
      samplesByEnvId,
      focusedSamplesByEnvId,
      environmentsForSelectedEnvTypeGroup,
      adminFeatures,
      sequentialSampleIndexes: {},
      consistentBioGroupNumbering: true,
      consistentBioNumbering: true,
      referenceData: undefined,
      getReferenceLevel: () => [null, null] as [null, null],
      tooManySamplesToShow: false,
    };
    return <ResearchContext.Provider value={contextData}>{children}</ResearchContext.Provider>;
  }
}

export function useResearchContext() {
  const context = useContext(ResearchContext);

  if (!context) {
    throw new Error('useResearchContext must be used within a ResearchProvider');
  }

  return context;
}

/**
 * Validates the structure and content of the sampling data.
 * @param {FullProject | null} project - The sampling data to validate.
 * @returns {boolean} - Returns true if the sampling data is valid, otherwise false.
 */
function validateProject(project: Partial<FullProject>): project is FullProject {
  // Example validation: Ensure that the sampling has necessary properties
  const requiredProperties = ['samplesByUID', 'focusedByUID'];
  const hasAllProperties = requiredProperties.every(prop => prop in project);

  if (!hasAllProperties) {
    return false;
  }

  // Further validations can be added here based on project requirements
  return true;
}
